import { IconBox } from "@/components/VisionUI"
import { useColorModeValue } from "@/components/ui/color-mode"
import { ReactIcon } from "@/components/ui/icon"
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu"
import {
  ALL_ORGS,
  isAllOrgs,
  useOrganization,
} from "@/contexts/OrganizationContext"
import useAuth from "@/hooks/useAuth"
import { colors, effects } from "@/theme"
import { Box, Flex, Spinner, Text } from "@chakra-ui/react"
import { useNavigate } from "@tanstack/react-router"
import { FiCheck, FiChevronDown, FiPlus } from "react-icons/fi"

interface OrganizationDropdownProps {
  collapsed?: boolean
}

export const OrganizationDropdown = ({
  collapsed = false,
}: OrganizationDropdownProps) => {
  const {
    currentOrganization,
    setCurrentOrganization,
    departments,
    isLoading,
  } = useOrganization()
  const { user } = useAuth()
  const navigate = useNavigate()
  const isLight = useColorModeValue(true, false)

  const triggerBg = isLight
    ? "rgba(0, 0, 0, 0.04)"
    : colors.glass.whiteAlpha[100]
  const triggerHoverBg = isLight
    ? "rgba(0, 0, 0, 0.08)"
    : colors.glass.whiteAlpha[200]
  const labelColor = isLight ? "#718096" : "gray.400"
  const nameColor = isLight ? "#1a202c" : "white"
  const menuBg = isLight ? "white" : effects.glass.cardBg
  const menuBorder = isLight ? "#E2E8F0" : colors.glass.whiteAlpha[200]
  const menuShadow = isLight
    ? "0 10px 30px rgba(0, 0, 0, 0.1)"
    : "0 10px 30px rgba(0, 0, 0, 0.3)"
  const itemHoverBg = isLight
    ? "rgba(0, 0, 0, 0.04)"
    : colors.glass.whiteAlpha[100]
  const descColor = isLight ? "#A0AEC0" : "gray.400"

  if (isLoading) {
    return (
      <Flex justify="center" p={4}>
        <Spinner size="sm" color={nameColor} />
      </Flex>
    )
  }

  if (collapsed) {
    return (
      <Box px={3} py={2}>
        <IconBox variant="glass" size="sm">
          <Text fontSize="xs" fontWeight="bold" color={nameColor}>
            {currentOrganization?.name?.substring(0, 2).toUpperCase() || "??"}
          </Text>
        </IconBox>
      </Box>
    )
  }

  return (
    <Box px={3} py={2}>
      <MenuRoot>
        <MenuTrigger asChild>
          <Box
            as="button"
            w="100%"
            p={3}
            borderRadius="12px"
            bg={triggerBg}
            _hover={{ bg: triggerHoverBg }}
            transition="all 0.2s"
            cursor="pointer"
          >
            <Flex justify="space-between" align="center">
              <Box textAlign="left">
                <Text
                  fontSize="xs"
                  color={labelColor}
                  fontWeight="500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Department
                </Text>
                <Text fontSize="sm" color={nameColor} fontWeight="600" mt={1}>
                  {currentOrganization?.name || "Select Department"}
                </Text>
              </Box>
              <ReactIcon
                icon={FiChevronDown}
                color={labelColor}
                fontSize="16px"
              />
            </Flex>
          </Box>
        </MenuTrigger>

        <MenuContent
          bg={menuBg}
          backdropFilter={isLight ? "none" : "blur(20px)"}
          border="1px solid"
          borderColor={menuBorder}
          borderRadius="12px"
          p={2}
          minW="200px"
          boxShadow={menuShadow}
        >
          {user?.is_superuser && (
            <>
              <MenuItem key="__all__" value="__all__" asChild>
                <Flex
                  as="button"
                  w="100%"
                  p={3}
                  borderRadius="8px"
                  _hover={{ bg: itemHoverBg }}
                  transition="all 0.2s"
                  justify="space-between"
                  align="center"
                  onClick={() => setCurrentOrganization(ALL_ORGS)}
                >
                  <Box textAlign="left">
                    <Text fontSize="sm" color="brand.200" fontWeight="600">
                      ⊕ All Departments
                    </Text>
                  </Box>
                  {isAllOrgs(currentOrganization) && (
                    <ReactIcon
                      icon={FiCheck}
                      color="brand.200"
                      fontSize="16px"
                    />
                  )}
                </Flex>
              </MenuItem>
              <MenuSeparator />
            </>
          )}
          {departments.map((org) => (
            <MenuItem key={org.id} value={org.id} asChild>
              <Flex
                as="button"
                w="100%"
                p={3}
                borderRadius="8px"
                _hover={{ bg: itemHoverBg }}
                transition="all 0.2s"
                justify="space-between"
                align="center"
                onClick={() => setCurrentOrganization(org)}
              >
                <Box textAlign="left">
                  <Text fontSize="sm" color={nameColor} fontWeight="500">
                    {org.name}
                  </Text>
                  {org.description && (
                    <Text fontSize="xs" color={descColor} mt={1}>
                      {org.description}
                    </Text>
                  )}
                </Box>
                {currentOrganization?.id === org.id && (
                  <ReactIcon icon={FiCheck} color="brand.200" fontSize="16px" />
                )}
              </Flex>
            </MenuItem>
          ))}

          {user?.is_superuser && (
            <>
              <MenuSeparator />
              <MenuItem value="manage" asChild>
                <Flex
                  as="button"
                  w="100%"
                  p={3}
                  borderRadius="8px"
                  _hover={{ bg: itemHoverBg }}
                  transition="all 0.2s"
                  align="center"
                  gap={2}
                  onClick={() => navigate({ to: "/organisations" })}
                >
                  <ReactIcon icon={FiPlus} color="brand.200" fontSize="16px" />
                  <Text fontSize="sm" color="brand.200" fontWeight="500">
                    Manage Departments
                  </Text>
                </Flex>
              </MenuItem>
            </>
          )}
        </MenuContent>
      </MenuRoot>
    </Box>
  )
}
